// src/components/ui/Avatar.tsx
import React, { useState } from 'react'
import { getPlaceholderAvatar } from '../../services/avatarService'

export type AvatarSize = 'sm' | 'md' | 'lg' | 'xl'

export interface AvatarProps {
  user?: {
    first_name?: string
    last_name?: string
    email?: string
    avatar_url?: string
  }
  size?: AvatarSize
  className?: string
  alt?: string
}

const sizeClasses: Record<AvatarSize, string> = {
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-12 h-12 text-base',
  xl: 'w-16 h-16 text-lg',
}

export const Avatar: React.FC<AvatarProps> = ({
  user,
  size = 'md',
  className = '',
  alt,
}) => {
  const [imageError, setImageError] = useState(false)

  // Get initials from name or email
  const getInitials = (): string => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase()
    }
    return 'U'
  }

  // Determine if we should show image or initials
  const shouldShowImage = user?.avatar_url && !imageError

  // Get fallback placeholder
  const placeholderUrl = getPlaceholderAvatar(
    user?.first_name && user?.last_name
      ? `${user.first_name}-${user.last_name}`
      : user?.email
  )

  const sizeClass = sizeClasses[size]
  const containerClasses = `${sizeClass} rounded-full flex items-center justify-center overflow-hidden ${className}`

  if (shouldShowImage) {
    return (
      <div className={containerClasses}>
        <img
          src={user.avatar_url}
          alt={alt || `${user.first_name || 'User'} avatar`}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
      </div>
    )
  }

  // Fallback to placeholder avatar or initials
  if (!imageError && placeholderUrl) {
    return (
      <div className={containerClasses}>
        <img
          src={placeholderUrl}
          alt={alt || `${user?.first_name || 'User'} avatar`}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
      </div>
    )
  }

  // Final fallback: show initials
  return (
    <div className={`${containerClasses} bg-brand-600 text-white font-medium`}>
      {getInitials()}
    </div>
  )
}
